import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

/**
 * What the router shows for a URL that matches no route.
 *
 * Without one, TanStack renders its own fallback — a bare "Not Found" on an
 * otherwise unstyled document, with no way out but the back button. A 404 is
 * reached by a stale bookmark or a mistyped path as often as by a real defect,
 * so this says which path missed and offers the two moves that answer both:
 * back to where the operator came from, or on to the landing page — which is
 * role-aware (`_auth/index`) and routes to `/login` when there is no session,
 * so it is a safe destination whoever is looking at this.
 */
export function NotFoundPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="epi-card tone-blue max-w-md text-center">
        <p className="label dash mb-3 text-left">
          {t('errors.notFound.label')}
        </p>
        <p className="display mb-2 text-[72px] text-blue">404</p>
        <h1 className="display mb-4 text-[36px]">
          {t('errors.notFound.title')}
        </h1>
        <p className="mb-4 text-muted">{t('errors.notFound.body')}</p>
        <p className="mono mb-6 text-[12px] break-all text-muted">{pathname}</p>
        <div className="flex items-center justify-center gap-3">
          <Link to="/" className="btn-blue">
            {t('common.backHome')}
          </Link>
          <button onClick={() => router.history.back()} className="btn-ghost">
            {t('common.back')}
          </button>
        </div>
      </div>
    </div>
  );
}
